import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  User,
  Pill,
  ArrowLeft,
  ClipboardList,
  FileText,
  Info,
  Trash2,
  Pencil,
  AlertCircle,
  Tag,
} from 'lucide-react';
import { toTitleCase } from '@/lib/utils';
import PractitionerSelection from './PrescriptionFormSheet/PractitionerSelection';
import type { Practitioner } from '@/lib/types';

export interface ReviewMedication {
  id?: string;
  medication_name: string;
  dose: string;
  sig: string;
  quantity: string;
  days_supply: string;
  refills: string;
  reason: string;
  price?: number;
  base_price?: number;
  discount?: number;
  program_name?: string;
  unit_of_measure?: string;
  flavor?: string;
  notes?: string;
  additional_context?: string;
  patient_contact_requested?: boolean;
  do_not_refill?: boolean;
  refill_needed?: boolean;
  do_not_fill?: boolean;
}

interface PrescriptionReviewScreenProps {
  medications: ReviewMedication[];
  patientName?: string;
  practitionerName?: string;
  showPractitionerDropdown?: boolean;
  selectedPractitionerId?: string;
  onPractitionerSelect?: (practitioner: Practitioner) => void;
  practitionerError?: string;
  practitionerLoading?: boolean;
  treatmentTypeEnabled?: boolean;
  treatmentType?: string;
  onTreatmentTypeChange?: (value: string) => void;
  priorityLevel: number;
  onPriorityChange: (priority: number) => void;
  onBack: () => void;
  onRemoveMedication?: (medication: ReviewMedication, index: number) => void;
  discount?: number;
  onDiscountChange?: (discount: number) => void;
  discountError?: boolean;
  priceWarningAcknowledged?: boolean;
  onPriceWarningAcknowledgedChange?: (acknowledged: boolean) => void;
}

const PrescriptionReviewScreen: React.FC<PrescriptionReviewScreenProps> = ({
  medications,
  patientName,
  practitionerName,
  showPractitionerDropdown = false,
  selectedPractitionerId = '',
  onPractitionerSelect,
  practitionerError,
  practitionerLoading = false,
  treatmentTypeEnabled = false,
  treatmentType = '',
  onTreatmentTypeChange,
  priorityLevel,
  onPriorityChange,
  onBack,
  onRemoveMedication,
  discount = 0,
  onDiscountChange,
  discountError = false,
  priceWarningAcknowledged = false,
  onPriceWarningAcknowledgedChange,
}) => {
  const [discountInput, setDiscountInput] = useState<string>(discount > 0 ? String(discount) : '');

  useEffect(() => {
    setDiscountInput(discount > 0 ? String(discount) : '');
  }, [discount]);

  const hasPriceWarning = medications.some(
    (med) =>
      med.price != null &&
      med.base_price != null &&
      med.base_price > 0 &&
      med.price < med.base_price
  );

  const subtotal = medications.reduce((sum, med) => {
    const price = Number(med.price) || 0;
    const qty = Number(med.quantity) || 1;
    return sum + price * qty;
  }, 0);

  // Maximum discount = sum of (patient_price - clinic_price) * qty across all items
  const maxDiscount = medications.reduce((sum, med) => {
    const price = Number(med.price) || 0;
    const basePrice = Number(med.base_price) || 0;
    const qty = Number(med.quantity) || 1;
    const margin = Math.max(0, price - basePrice);
    return sum + margin * qty;
  }, 0);

  const totalAfterDiscount = subtotal - discount;

  return (
    <div className="space-y-6">
      {/* Review Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          className="text-slate-600 hover:text-slate-900 -ml-2"
          onClick={onBack}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Edit
        </Button>
      </div>

      <div className="bg-gradient-to-r from-brand-teal/5 to-brand-teal/10 rounded-xl p-5 border border-brand-teal/20">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-brand-teal/20 rounded-xl flex items-center justify-center">
            <ClipboardList className="h-5 w-5 text-brand-teal" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 text-lg">Review Order</h3>
            <p className="text-sm text-slate-600">
              {medications.length} medication{medications.length !== 1 ? 's' : ''} ready to submit
            </p>
          </div>
        </div>
      </div>

      {/* Patient & Practitioner Summary */}
      <div className={`grid gap-4 ${showPractitionerDropdown && onPractitionerSelect ? 'grid-cols-1' : 'grid-cols-2'}`}>
        {/* Patient Info */}
        <div className="bg-slate-50 rounded-lg border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-brand-teal/10 rounded-lg flex items-center justify-center">
              <User className="h-4 w-4 text-brand-teal" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Patient</p>
              <p className="font-medium text-slate-900 text-sm truncate">
                {patientName ? toTitleCase(patientName) : 'Selected Patient'}
              </p>
            </div>
          </div>
        </div>

        {/* Practitioner: dropdown (medication_select) OR default-label chip (protocol_showcase) */}
        {showPractitionerDropdown && onPractitionerSelect ? (
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <PractitionerSelection
              selectedPractitionerId={selectedPractitionerId}
              onPractitionerSelect={onPractitionerSelect}
              isLoading={practitionerLoading}
              error={practitionerError}
            />
          </div>
        ) : (
          <div className="bg-slate-50 rounded-lg border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center">
                <User className="h-4 w-4 text-purple-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Practitioner</p>
                <p className="font-medium text-slate-900 text-sm truncate">
                  {practitionerName || 'Default Practitioner — assigned by system'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Treatment Type Selector */}
      {treatmentTypeEnabled && onTreatmentTypeChange && (
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium text-slate-700">
                Treatment Type <span className="text-red-500">*</span>
              </Label>
              <p className="text-xs text-slate-500 mt-0.5">
                Select the treatment category for this prescription
              </p>
            </div>
            <Select
              value={treatmentType || undefined}
              onValueChange={onTreatmentTypeChange}
            >
              <SelectTrigger className="w-[260px] h-10 bg-white border-slate-200">
                <SelectValue placeholder="Select treatment type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="trt">TRT (Testosterone Replacement)</SelectItem>
                <SelectItem value="hrt">HRT (Hormone Replacement)</SelectItem>
                <SelectItem value="glp1">GLP-1 (Weight Management)</SelectItem>
                <SelectItem value="peptide">Peptide Therapy</SelectItem>
                <SelectItem value="nad">NAD+ Therapy</SelectItem>
                <SelectItem value="fertility">Fertility</SelectItem>
                <SelectItem value="general">General</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Priority Level Selector */}
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm font-medium text-slate-700">Priority Level</Label>
            <p className="text-xs text-slate-500 mt-0.5">Set the urgency of this prescription</p>
          </div>
          <Select
            value={String(priorityLevel)}
            onValueChange={(value) => onPriorityChange(Number(value))}
          >
            <SelectTrigger className="w-[180px] h-10 bg-white border-slate-200">
              <SelectValue placeholder="Select priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="-1">
                <span className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-slate-400"></span>
                  Low Precedence
                </span>
              </SelectItem>
              <SelectItem value="1">
                <span className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-green-500"></span>
                  Normal
                </span>
              </SelectItem>
              <SelectItem value="2">
                <span className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span>
                  Elevated
                </span>
              </SelectItem>
              <SelectItem value="3">
                <span className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>
                  Urgent
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Medications List */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          Medications ({medications.length})
        </h4>

        <div className="space-y-3">
          {medications.map((med, index) => (
            <motion.div
              key={med.id || index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05, duration: 0.2 }}
            >
              <Card className="border-slate-200 overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Pill className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h5 className="font-semibold text-slate-900 text-sm">
                            {med.medication_name}
                          </h5>
                          {med.program_name && (
                            <p className="text-xs text-slate-500 mt-0.5">{med.program_name}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {med.price !== undefined && med.price > 0 && (
                            <Badge className="bg-green-100 text-green-700 border-0 font-semibold">
                              ${((med.price || 0) * (Number(med.quantity) || 1)).toFixed(2)}
                            </Badge>
                          )}
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  aria-label="Edit prescription"
                                  className="h-8 w-8 text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                                  onClick={onBack}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Edit Rx</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          {onRemoveMedication && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    aria-label="Remove prescription"
                                    className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => onRemoveMedication(med, index)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Remove Rx</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </div>

                      {/* Details Grid */}
                      <div className="grid grid-cols-5 gap-3 mt-3">
                        <div className="bg-slate-50 rounded-md p-2">
                          <span className="text-xs text-slate-500 block">Dose</span>
                          <p className="font-medium text-slate-900 text-xs truncate" title={med.dose}>
                            {med.dose || '-'}
                          </p>
                        </div>
                        <div className="bg-slate-50 rounded-md p-2">
                          <span className="text-xs text-slate-500 block">Quantity</span>
                          <p className="font-medium text-slate-900 text-xs">{med.quantity}</p>
                        </div>
                        <div className="bg-slate-50 rounded-md p-2">
                          <span className="text-xs text-slate-500 block">Unit</span>
                          <p className="font-medium text-slate-900 text-xs truncate" title={med.unit_of_measure}>
                            {med.unit_of_measure || '-'}
                          </p>
                        </div>
                        <div className="bg-slate-50 rounded-md p-2">
                          <span className="text-xs text-slate-500 block">Days Supply</span>
                          <p className="font-medium text-slate-900 text-xs">{med.days_supply}</p>
                        </div>
                        <div className="bg-slate-50 rounded-md p-2">
                          <span className="text-xs text-slate-500 block">Refills</span>
                          <p className="font-medium text-slate-900 text-xs">{med.refills}</p>
                        </div>
                      </div>

                      {/* Instructions */}
                      {med.sig && (
                        <div className="mt-3 p-2.5 bg-blue-50 rounded-md border border-blue-100">
                          <div className="flex items-start gap-2">
                            <FileText className="h-3.5 w-3.5 text-blue-500 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-blue-800 leading-relaxed">{med.sig}</p>
                          </div>
                        </div>
                      )}

                      {/* Reason */}
                      {med.reason && (
                        <div className="mt-2 flex items-start gap-2">
                          <Info className="h-3.5 w-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-slate-600">{med.reason}</p>
                        </div>
                      )}

                      {/* Additional Details */}
                      {(med.flavor || med.notes || med.additional_context) && (
                        <div className="mt-3 grid grid-cols-2 gap-2">
                          {med.flavor && (
                            <div className="bg-slate-50 rounded-md p-2">
                              <span className="text-xs text-slate-500 block">Flavor</span>
                              <p className="font-medium text-slate-900 text-xs">{med.flavor}</p>
                            </div>
                          )}
                          {med.notes && (
                            <div className="bg-slate-50 rounded-md p-2">
                              <span className="text-xs text-slate-500 block">Notes</span>
                              <p className="font-medium text-slate-900 text-xs">{med.notes}</p>
                            </div>
                          )}
                          {med.additional_context && (
                            <div className="bg-slate-50 rounded-md p-2 col-span-2">
                              <span className="text-xs text-slate-500 block">Additional Context</span>
                              <p className="font-medium text-slate-900 text-xs">{med.additional_context}</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Flags */}
                      {(med.patient_contact_requested || med.do_not_refill || med.refill_needed || med.do_not_fill) && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {med.patient_contact_requested && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                              Patient Contact Requested
                            </span>
                          )}
                          {med.refill_needed && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              Refill Needed
                            </span>
                          )}
                          {med.do_not_refill && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                              Do Not Refill
                            </span>
                          )}
                          {med.do_not_fill && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                              Do Not Fill
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Discount & Total Section */}
      {subtotal > 0 && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-5 space-y-3">
          {/* Subtotal */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">Subtotal</span>
            <span className="text-sm font-medium text-slate-900">${subtotal.toFixed(2)}</span>
          </div>

          {/* Discount Input */}
          {onDiscountChange && maxDiscount > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Tag className="h-4 w-4 text-slate-500" />
                  <span className="text-sm text-slate-600">Discount</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">$</span>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={discountInput}
                    placeholder="0.00"
                    onKeyDown={(e) => {
                      if (!/[0-9.]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
                        e.preventDefault();
                      }
                      if (e.key === '.' && (e.target as HTMLInputElement).value.includes('.')) {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === '' || /^\d*\.?\d*$/.test(raw)) {
                        setDiscountInput(raw);
                        const parsed = parseFloat(raw);
                        onDiscountChange(isNaN(parsed) ? 0 : parsed);
                      }
                    }}
                    className={`w-28 h-9 text-right text-sm ${
                      discountError ? 'border-red-500 focus-visible:ring-red-500' : ''
                    }`}
                  />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-400">
                  Maximum discount: ${maxDiscount.toFixed(2)}
                </span>
                {discountError && (
                  <span className="text-xs text-red-600 font-medium flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Discount exceeds maximum
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Discount line (if applied) */}
          {discount > 0 && !discountError && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-emerald-600">Discount</span>
              <span className="text-sm font-medium text-emerald-600">-${discount.toFixed(2)}</span>
            </div>
          )}

          {/* Separator */}
          <div className="border-t border-slate-200" />

          {/* Total */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-700">Estimated Total</span>
            <span className="text-lg font-bold text-slate-900">
              ${(discountError ? subtotal : totalAfterDiscount).toFixed(2)}
            </span>
          </div>
        </div>
      )}

      {/* Price warning: patient price lower than pharmacy cost */}
      {hasPriceWarning && onPriceWarningAcknowledgedChange && (
        <div className="flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
            <span className="text-sm text-amber-800">
              One or more medications have a patient price lower than the pharmacy cost. The clinic may lose money on this order. Please review pricing in Settings → Products before proceeding.
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="price-warning-ack-review"
              checked={priceWarningAcknowledged}
              onCheckedChange={(checked) => onPriceWarningAcknowledgedChange(!!checked)}
            />
            <Label
              htmlFor="price-warning-ack-review"
              className="text-sm font-medium text-amber-800 cursor-pointer"
            >
              I understand and want to proceed with creating this prescription
            </Label>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrescriptionReviewScreen;
